import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '~/lib/api';
import { formatLongDate } from '~/lib/date';
import { messages, useT } from '~/i18n';
import { EmptyState, PageHeader, Tone, WaitingInline } from '~/components/ui';
import EnglishAssessmentsSection from './components/EnglishAssessmentsSection';

/**
 * Contact invitations — the candidate's side of the agency matchmaking.
 *
 * Serves both `/invitations` (the inbox) and `/invitations/:matchId`, which is
 * the link the invitation email points at. There is no GET for a single
 * invitation, so the deep link loads the list with `status=all` and focuses one
 * row: an invitation already answered from another device then shows its answer
 * instead of a pair of buttons that would 409.
 *
 * Accepting here is the ONLY thing that hands an agency the candidate's email.
 * Nothing on the agency side can do it, which is why the copy says so.
 */

const PENDING = 'contact_requested';

interface Invitation {
  _id: string;
  status: string;
  invited_at: string | null;
  responded_at: string | null;
  agency: { name: string; country: string | null };
  position: { _id: string; title: string; company: string; location: string };
}

/**
 * La fecha larga, y vacía cuando no hay: acá va DENTRO de una frase ("Te
 * invitaron el ..."), donde la raya de "no sabemos" se leería peor que no decir
 * nada. Por eso el fallback explícito y no el de la casa.
 */
function formatDate(value: string | null): string {
  return formatLongDate(value, '');
}

export default function JobApplyInvitationsPage() {
  const { matchId } = useParams<{ matchId?: string }>();
  // `useT` se suscribe al idioma; `~/lib/date` lo lee al llamarse.
  const ws = useT('workspace');
  const t = ws.invitations;
  const [invitations, setInvitations] = useState<Invitation[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // status=all: a deep link has to be able to show an invitation that is no
      // longer pending, and the inbox reads better with its own history.
      const res = await apiFetch('/job-apply/api/invitations?status=all');
      if (!res.ok) {
        setErr(messages().workspace.invitations.loadError);
        return;
      }
      const body = await res.json();
      setInvitations(Array.isArray(body?.invitations) ? body.invitations : []);
      setErr(null);
    } catch {
      setErr(messages().workspace.invitations.connection);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const respond = async (invitation: Invitation, accept: boolean) => {
    setBusyId(invitation._id);
    setErr(null);
    setNotice(null);
    try {
      const res = await apiFetch(`/job-apply/api/invitations/${invitation._id}/respond`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ accept }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setErr(t.alreadyAnswered);
        await load();
        return;
      }
      if (!res.ok) {
        setErr(body?.error || t.respondFailed);
        return;
      }
      setInvitations((prev) =>
        (prev ?? []).map((item) =>
          item._id === invitation._id
            ? { ...item, status: body?.invitation?.status ?? item.status, responded_at: body?.invitation?.responded_at ?? null }
            : item,
        ),
      );
      setNotice(
        accept
          ? t.acceptedNotice(invitation.agency.name)
          : t.declinedNotice,
      );
    } catch {
      setErr(t.connection);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <WaitingInline label={t.loading} />;

  const all = invitations ?? [];
  const focused = matchId ? all.find((item) => item._id === matchId) : undefined;
  const list = focused ? [focused] : all;
  const pending = all.filter((item) => item.status === PENDING);

  return (
    <div className="space-y-6">
      <PageHeader
        scope="TBit Jobs"
        title={focused ? t.focusedTitle : t.title}
        actions={
          focused ? (
            <Link to="/invitations" className="btn btn-ghost btn-sm">{t.seeAll}</Link>
          ) : undefined
        }
      />

      {err && <div className="alert alert-error text-sm">{err}</div>}
      {notice && <div className="alert alert-success text-sm">{notice}</div>}

      {/* Una empresa que ya tiene tu contacto puede pedirte la evaluación de inglés. */}
      {!matchId && <EnglishAssessmentsSection />}

      {matchId && !focused && (
        <EmptyState
          icon="search"
          title={t.notFoundTitle}
          note={t.notFoundNote}
          action={
            <Link to="/invitations" className="btn btn-primary btn-sm">{t.seeMine}</Link>
          }
        />
      )}

      {!focused && all.length === 0 && (
        <EmptyState
          icon="bubble"
          title={t.emptyTitle}
          note={t.emptyNote}
          action={
            <Link to="/perfil/bolsa-de-talento" className="btn btn-primary btn-sm">
              {t.activateTalentPool}
            </Link>
          }
        />
      )}

      {!focused && all.length > 0 && pending.length === 0 && (
        <div className="alert text-sm">
          <span>{t.noPending}</span>
        </div>
      )}

      <div className="space-y-3">
        {list.map((invitation) => {
          const isPending = invitation.status === PENDING;
          return (
            <div key={invitation._id} className="card rise">
              <div className="card-body space-y-2">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="card-title text-lg">{invitation.position.title}</h2>
                    <p className="text-sm hush">
                      {invitation.position.company}
                      {invitation.position.location && <> · {invitation.position.location}</>}
                    </p>
                    <p className="text-sm mt-1">
                      {t.agency}{' '}<span className="font-medium">{invitation.agency.name}</span>
                      {invitation.agency.country && (
                        <span className="hush"> ({invitation.agency.country})</span>
                      )}
                    </p>
                    {invitation.invited_at && (
                      <p className="text-xs hush mt-1">
                        {t.invitedOn(formatDate(invitation.invited_at))}
                      </p>
                    )}
                  </div>
                  {!isPending && (
                    <Tone tone={invitation.status === 'candidate_accepted' ? 'good' : 'muted'}>
                      {invitation.status === 'candidate_accepted' ? ws.invitation.accepted : ws.invitation.declined}
                    </Tone>
                  )}
                </div>

                {isPending ? (
                  <>
                    <p className="text-sm hush">{t.pendingNote}</p>
                    <div className="card-actions justify-end">
                      {busyId === invitation._id && (
                        <span className="loading loading-spinner loading-sm self-center" />
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={busyId === invitation._id}
                        onClick={() => respond(invitation, false)}
                      >
                        {t.decline}
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={busyId === invitation._id}
                        onClick={() => respond(invitation, true)}
                      >
                        {t.accept}
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm hush">
                    {invitation.status === 'candidate_accepted'
                      ? t.sharedOn(invitation.agency.name, formatDate(invitation.responded_at))
                      : ws.invitation.notShared}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
